/**
 * Client API léger pour le portail étudiant (sans Redux).
 * Stocke le token dans localStorage sous 'portail_access_token'.
 */
const API_BASE = process.env.REACT_APP_API_URL || '/api';
const TOKEN_KEY = 'portail_access_token';
const ETUDIANT_KEY = 'portail_etudiant';

export interface EtudiantInfo {
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  niveau: string;
  option: string;
  parcours: string;
  pole: string;
  photo_url: string | null;
}

export interface AccesDetail {
  periode: string;
  present: boolean;
  est_a_jour: boolean;
  solde: string;
  frais_attendus: string;
  frais_payes: string;
}

export interface AccesResultats {
  autorise: boolean;
  mode: 'a_jour' | 'force_admin' | 'sous_contrat' | 'bloque_dette' | 'bloque_absent';
  details: AccesDetail[];
  solde_total: string;
  nb_periodes_requises: number;
  nb_a_jour: number;
  nb_present: number;
  // Accès léger (2026-09-12) : seules des périodes en accès léger sont impayées
  acces_leger?: boolean;
  periodes_leger_dues?: string[];
}

// Relevé réduit de l'accès léger : décision + matières à rattraper, sans notes
export interface ReleveLeger {
  semestre: number;
  annee_academique: string;
  etudiant: { matricule: string; nom: string; prenoms: string; classe: string; niveau: string; option: string };
  periodes_dues: { periode: string; label: string; present: boolean; solde: string | null }[];
  decision_disponible: boolean;
  message?: string;
  valide?: boolean;
  ues_a_rattraper?: {
    code_ue: string;
    nom_ue: string;
    prioritaire: boolean;
    matieres: { code_matiere: string; nom_matiere: string }[];
  }[];
}

export interface StatutPaiementMois {
  periode: string;
  label?: string;
  present: boolean;
  // Aménagement (2026-09-12) : false = période qui ne concerne pas l'étudiant
  concernee?: boolean;
  statut?: 'a_jour' | 'reliquat' | 'impaye' | 'non_enregistre' | 'non_concerne';
  montant_exige?: string | null;
  est_a_jour: boolean;
  solde: string;
  frais_attendus: string;
  frais_payes: string;
}

export interface MeResponse {
  etudiant: EtudiantInfo;
  acces_resultats: AccesResultats;
  statut_paiements?: StatutPaiementMois[];
  nb_notifications_non_lues: number;
  securite?: {
    question_definie: boolean;
    mdp_initial_defini: boolean;
  };
}

export interface MatriculeResult {
  matricule_masque: string;
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
}

export const portailApi = {
  setToken(token: string) {
    localStorage.setItem(TOKEN_KEY, token);
  },
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  clearToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ETUDIANT_KEY);
  },
  setEtudiant(etu: { matricule: string; nom: string; prenoms: string }) {
    localStorage.setItem(ETUDIANT_KEY, JSON.stringify(etu));
  },
  getEtudiant() {
    const s = localStorage.getItem(ETUDIANT_KEY);
    if (!s) return null;
    try { return JSON.parse(s); } catch { return null; }
  },

  async _fetch(path: string, options: RequestInit = {}) {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    // Pour cohérence avec le backoffice : envoyer aussi l'année si connue
    const annee = localStorage.getItem('annee_academique');
    if (annee) headers['X-Annee-Academique'] = annee;

    const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
    let data: any = null;
    try { data = await res.json(); } catch { data = null; }
    // Mode maintenance global : redirige vers la page dédiée
    if (res.status === 503 && data?.maintenance) {
      try {
        sessionStorage.setItem('portail_maintenance', JSON.stringify({
          message: data.message || data.detail || '',
          depuis: data.depuis || null,
        }));
        if (window.location.pathname !== '/portail/maintenance') {
          window.location.href = '/portail/maintenance';
        }
      } catch { /* ignore */ }
    }
    if (!res.ok) {
      const detail = data?.detail || data?.error || `Erreur ${res.status}`;
      const err: any = new Error(detail);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  },

  async getMaintenanceStatus(): Promise<{ actif: boolean; message: string; depuis: string | null }> {
    const res = await fetch(`${API_BASE}/portail/maintenance-status/`);
    if (!res.ok) return { actif: false, message: '', depuis: null };
    return res.json();
  },

  async findMatricule(payload: { nom: string; prenoms?: string; date_naissance: string }) {
    return this._fetch('/portail/auth/find-matricule/', {
      method: 'POST',
      body: JSON.stringify(payload),
    }) as Promise<{ results: MatriculeResult[] }>;
  },

  async firstLogin(payload: { matricule: string; date_naissance: string }) {
    return this._fetch('/portail/auth/first-login/', {
      method: 'POST',
      body: JSON.stringify(payload),
    }) as Promise<{
      reset_token: string;
      has_password: boolean;
      nom: string;
      prenoms: string;
      requires_question_securite?: boolean;
    }>;
  },

  async setPassword(payload: {
    reset_token: string;
    password: string;
    question_securite?: string;
    reponse_securite?: string;
  }) {
    return this._fetch('/portail/auth/set-password/', {
      method: 'POST',
      body: JSON.stringify(payload),
    }) as Promise<{ access_token: string; matricule: string; nom: string; prenoms: string }>;
  },

  // Reset sécurisé (après 1re activation)
  async resetInit(payload: { matricule: string }) {
    return this._fetch('/portail/auth/reset-init/', {
      method: 'POST',
      body: JSON.stringify(payload),
    }) as Promise<{ question_securite: string }>;
  },

  async resetVerify(payload: {
    matricule: string;
    date_naissance: string;
    reponse_securite: string;
  }) {
    return this._fetch('/portail/auth/reset-verify/', {
      method: 'POST',
      body: JSON.stringify(payload),
    }) as Promise<{ reset_token: string; nom: string; prenoms: string }>;
  },

  // ─── Anniversaires sociaux ────────────────────────────────
  async anniversairesDuJour() {
    return this._fetch('/portail/me/anniversaires-du-jour/') as Promise<{
      results: Array<{
        matricule: string;
        nom: string;
        prenoms: string;
        classe: string;
        photo_url: string | null;
        age: number | null;
        nb_messages: number;
        est_moi: boolean;
      }>;
      nb_total: number;
    }>;
  },
  async anniversaireMessages(matricule: string) {
    return this._fetch(`/portail/me/anniversaires/${matricule}/messages/`) as Promise<{
      destinataire: any;
      messages: Array<{
        id: number;
        auteur: {
          nom: string;
          prenoms: string;
          matricule: string;
          matricule_reel?: string;
          photo_url: string | null;
          est_admin: boolean;
          classe?: string;
        };
        contenu: string;
        type_souhait: 'message' | 'like';
        date_creation: string;
        nb_likes: number;
        a_like: boolean;
        est_de_moi: boolean;
      }>;
      nb_total: number;
    }>;
  },
  async uploadSaveTheDate(file: File): Promise<{ success: boolean; photo_url: string | null }> {
    // Upload multipart (image) — écrase toute photo existante
    const token = this.getToken();
    const fd = new FormData();
    fd.append('photo', file);
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/portail/me/save-the-date/photo/`, {
      method: 'POST', body: fd, headers,
    });
    const data = await res.json();
    if (!res.ok) throw Object.assign(new Error(data?.detail || 'Erreur upload'), { status: res.status });
    return data;
  },
  async deleteSaveTheDate(): Promise<{ success: boolean }> {
    return this._fetch('/portail/me/save-the-date/photo/', {
      method: 'DELETE',
    }) as Promise<{ success: boolean }>;
  },

  async anniversaireSouhaiter(matricule: string, payload: { contenu: string; type_souhait?: 'message' | 'like' }) {
    return this._fetch(`/portail/me/anniversaires/${matricule}/souhaiter/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async anniversaireMessageLike(msgId: number, like: boolean) {
    return this._fetch(`/portail/me/anniversaires/messages/${msgId}/like/`, {
      method: like ? 'POST' : 'DELETE',
    }) as Promise<{ nb_likes: number; a_like: boolean }>;
  },
  async anniversaireMessageSupprimer(msgId: number) {
    return this._fetch(`/portail/me/anniversaires/messages/${msgId}/`, {
      method: 'DELETE',
    }) as Promise<{ success: boolean }>;
  },
  async anniversaireCommentaires(msgId: number) {
    return this._fetch(
      `/portail/me/anniversaires/messages/${msgId}/commentaires/`,
    ) as Promise<{ results: any[]; nb_total: number }>;
  },
  async anniversaireCommentaireCreer(msgId: number, payload: { contenu: string; parent_id?: number }) {
    return this._fetch(`/portail/me/anniversaires/messages/${msgId}/commentaires/creer/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async anniversaireCommentaireSupprimer(cId: number) {
    return this._fetch(`/portail/me/anniversaires/commentaires/${cId}/`, {
      method: 'DELETE',
    }) as Promise<{ success: boolean }>;
  },
  async anniversaireCommentaireLike(cId: number, like: boolean) {
    return this._fetch(`/portail/me/anniversaires/commentaires/${cId}/like/`, {
      method: like ? 'POST' : 'DELETE',
    }) as Promise<{ nb_likes: number; a_like: boolean }>;
  },
  async anniversairesProchains(jours = 5) {
    return this._fetch(
      `/portail/me/anniversaires-prochains/?jours=${jours}`,
    ) as Promise<{
      jours: number;
      par_date: Record<string, Array<{
        matricule: string; nom: string; prenoms: string; classe: string;
        photo_url: string | null; age: number | null;
        date_anniversaire: string; jours_restants: number;
      }>>;
      nb_total: number;
    }>;
  },

  // ─── WebAuthn / biométrie ─────────────────────────────────
  async webauthnHasCredentials(matricule: string) {
    return this._fetch(
      `/portail/auth/webauthn/has-credentials/?matricule=${encodeURIComponent(matricule)}`,
    ) as Promise<{ has_credentials: boolean }>;
  },
  async webauthnLoginBegin(matricule: string) {
    return this._fetch('/portail/auth/webauthn/login/begin/', {
      method: 'POST',
      body: JSON.stringify({ matricule }),
    }) as Promise<any>;
  },
  async webauthnLoginComplete(matricule: string, credential: any) {
    return this._fetch('/portail/auth/webauthn/login/complete/', {
      method: 'POST',
      body: JSON.stringify({ matricule, credential }),
    }) as Promise<{ access_token: string; matricule: string; nom: string; prenoms: string }>;
  },
  async webauthnRegisterBegin() {
    return this._fetch('/portail/me/webauthn/register/begin/', {
      method: 'POST',
      body: JSON.stringify({}),
    }) as Promise<any>;
  },
  async webauthnRegisterComplete(credential: any, nom_appareil: string) {
    return this._fetch('/portail/me/webauthn/register/complete/', {
      method: 'POST',
      body: JSON.stringify({ credential, nom_appareil }),
    }) as Promise<{ success: boolean; credential_id: number; nom_appareil: string }>;
  },
  async webauthnListCredentials() {
    return this._fetch('/portail/me/webauthn/credentials/') as Promise<{
      results: Array<{
        id: number;
        nom_appareil: string;
        date_creation: string;
        derniere_utilisation: string | null;
        transports: string[];
      }>;
      total: number;
    }>;
  },
  async webauthnDeleteCredential(id: number) {
    return this._fetch(`/portail/me/webauthn/credentials/${id}/`, {
      method: 'DELETE',
    }) as Promise<{ success: boolean }>;
  },
  async webauthnRenameCredential(id: number, nom_appareil: string) {
    return this._fetch(`/portail/me/webauthn/credentials/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify({ nom_appareil }),
    }) as Promise<{ success: boolean; nom_appareil: string }>;
  },

  // Question de sécurité (depuis Réglages)
  async getQuestionSecurite() {
    return this._fetch('/portail/me/question-securite/') as Promise<{
      question_securite: string;
      has_reponse: boolean;
    }>;
  },

  async setQuestionSecurite(payload: {
    ancien_password: string;
    question: string;
    reponse: string;
  }) {
    return this._fetch('/portail/me/question-securite/', {
      method: 'POST',
      body: JSON.stringify(payload),
    }) as Promise<{ success: boolean }>;
  },

  async login(payload: { matricule: string; password: string }) {
    return this._fetch('/portail/auth/login/', {
      method: 'POST',
      body: JSON.stringify(payload),
    }) as Promise<{ access_token: string; matricule: string; nom: string; prenoms: string }>;
  },

  async me() {
    return this._fetch('/portail/me/') as Promise<MeResponse>;
  },

  async releve(semestre: number = 1, annee?: string) {
    const q = annee ? `&annee=${encodeURIComponent(annee)}` : '';
    return this._fetch(`/portail/me/releve/?semestre=${semestre}${q}`) as Promise<ReleveData>;
  },

  // Années académiques où l'étudiant a des résultats (sélecteur d'historique).
  async annees() {
    return this._fetch('/portail/me/annees/') as Promise<{
      annees: { annee: string; niveau: string; courante: boolean; a_resultats: boolean }[];
      courante: string | null;
    }>;
  },

  async deposerReclamation(payload: {
    type_reclamation: 'devoir' | 'examen' | 'rattrapage' | 'autre';
    matiere_id?: number | null;
    matiere_nom?: string;
    semestre?: string;
    note_actuelle?: number | null;
    note_attendue?: number | null;
    motif: string;
  }) {
    return this._fetch('/portail/me/reclamations/deposer/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async mesReclamations() {
    return this._fetch('/portail/me/reclamations/') as Promise<{
      results: ReclamationItem[];
      fenetre: FenetreReclamation;
      limite_par_semestre: number;
      nb_total_non_lus: number;
    }>;
  },

  async getReclamationMessages(id: number) {
    return this._fetch(`/portail/me/reclamations/${id}/messages/`) as Promise<{
      results: MessageReclamationItem[];
      reclamation: { id: number; statut: string; cloturee: boolean; matiere_nom: string; reponse_scolarite: string };
    }>;
  },

  async sendReclamationMessage(id: number, contenu: string) {
    return this._fetch(`/portail/me/reclamations/${id}/messages/`, {
      method: 'POST',
      body: JSON.stringify({ contenu }),
    }) as Promise<{
      results: MessageReclamationItem[];
      reclamation: { id: number; statut: string; cloturee: boolean; matiere_nom: string };
    }>;
  },

  async notifications() {
    return this._fetch('/portail/me/notifications/') as Promise<{
      results: NotificationItem[];
      nb_non_lues: number;
    }>;
  },

  async marquerNotifLue(id: number) {
    return this._fetch(`/portail/me/notifications/${id}/lue/`, { method: 'POST' });
  },

  async marquerToutesLues() {
    return this._fetch('/portail/me/notifications/marquer-toutes-lues/', {
      method: 'POST',
    }) as Promise<{ success: true; nb_marquees: number }>;
  },

  async supprimerNotifications(ids: number[]) {
    return this._fetch('/portail/me/notifications/supprimer/', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }) as Promise<{ success: true; nb_supprimees: number }>;
  },

  async getPushVapidKey() {
    return this._fetch('/portail/push-vapid-key/') as Promise<{ public_key: string }>;
  },

  async pushSubscribe(subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) {
    return this._fetch('/portail/me/push-subscribe/', {
      method: 'POST',
      body: JSON.stringify(subscription),
    }) as Promise<{ success: true }>;
  },

  async pushUnsubscribe(endpoint: string) {
    return this._fetch('/portail/me/push-unsubscribe/', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    }) as Promise<{ success: true }>;
  },

  async testPush() {
    return this._fetch('/portail/me/test-push/', {
      method: 'POST',
    }) as Promise<{
      success: true;
      nb_subscriptions_actives: number;
      nb_pushs_envoyees: number;
    }>;
  },

  async soumettreDemandeActivation(formData: FormData) {
    const token = this.getToken();
    const annee = localStorage.getItem('annee_academique');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (annee) headers['X-Annee-Academique'] = annee;
    const res = await fetch(`${API_BASE}/portail/me/demande-activation/`, {
      method: 'POST',
      body: formData, // multipart/form-data — ne PAS définir Content-Type
      headers,
    });
    let data: any = null;
    try { data = await res.json(); } catch { data = null; }
    if (!res.ok) {
      const detail = data?.detail || `Erreur ${res.status}`;
      const err: any = new Error(detail);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data as { success: true; demande_id: number; statut: string };
  },

  async mesDemandesActivation() {
    return this._fetch('/portail/me/demandes-activation/') as Promise<{
      results: DemandeActivationItem[];
    }>;
  },

  async supprimerDemandeActivation(demandeId: number) {
    return this._fetch(`/portail/me/demandes-activation/${demandeId}/`, {
      method: 'DELETE',
    }) as Promise<{ success: true }>;
  },

  async uploadPhotoProfil(photo: File) {
    const token = this.getToken();
    const annee = localStorage.getItem('annee_academique');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (annee) headers['X-Annee-Academique'] = annee;
    const fd = new FormData();
    fd.append('photo', photo);
    const res = await fetch(`${API_BASE}/portail/me/photo-profil/`, {
      method: 'POST', body: fd, headers,
    });
    let data: any = null;
    try { data = await res.json(); } catch { data = null; }
    if (!res.ok) {
      const err: any = new Error(data?.detail || `Erreur ${res.status}`);
      err.status = res.status; err.data = data;
      throw err;
    }
    return data as { success: true; photo_url: string };
  },

  async deletePhotoProfil() {
    return this._fetch('/portail/me/photo-profil/', { method: 'DELETE' }) as Promise<{
      success: true; photo_url: string | null;
    }>;
  },

  // ─── CHAT étudiants ↔ étudiants ─────────────────────────────
  async chatConversations() {
    return this._fetch('/portail/me/chat/conversations/') as Promise<{
      results: ChatConversationItem[];
      total_non_lus: number;
    }>;
  },
  async chatOuvrirDirecte(matricule_destinataire: string) {
    return this._fetch('/portail/me/chat/ouvrir/', {
      method: 'POST',
      body: JSON.stringify({ matricule_destinataire }),
    }) as Promise<{ conversation_id: number; cree: boolean; destinataire: ChatEtudiantSummary }>;
  },
  async chatMessages(convId: number) {
    return this._fetch(`/portail/me/chat/conversations/${convId}/messages/`) as Promise<{
      conversation: { id: number; type: string; nom: string; autres_participants: ChatEtudiantSummary[]; en_ligne: boolean };
      messages: ChatMessageItem[];
      epingles?: ChatEpingleItem[];
      typing?: { matricule: string; action: 'typing' | 'recording' }[];
    }>;
  },
  async chatEnvoyerMessage(convId: number, contenu: string, replyToId?: number) {
    return this._fetch(`/portail/me/chat/conversations/${convId}/messages/`, {
      method: 'POST',
      body: JSON.stringify({ contenu, ...(replyToId ? { reply_to_id: replyToId } : {}) }),
    }) as Promise<{ id: number; contenu: string; date_envoi: string; est_de_moi: boolean }>;
  },
  async chatEnvoyerFichier(convId: number, fichier: File, contenu = '') {
    const token = this.getToken();
    const annee = localStorage.getItem('annee_academique');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (annee) headers['X-Annee-Academique'] = annee;
    const fd = new FormData();
    fd.append('fichier', fichier);
    if (contenu) fd.append('contenu', contenu);
    const res = await fetch(`${API_BASE}/portail/me/chat/conversations/${convId}/messages/`, {
      method: 'POST', body: fd, headers,
    });
    let data: any = null;
    try { data = await res.json(); } catch { data = null; }
    if (!res.ok) {
      const err: any = new Error(data?.detail || `Erreur ${res.status}`);
      err.status = res.status; err.data = data;
      throw err;
    }
    return data;
  },
  async chatEnvoyerAudio(convId: number, audioBlob: Blob, dureeSec: number) {
    const token = this.getToken();
    const annee = localStorage.getItem('annee_academique');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (annee) headers['X-Annee-Academique'] = annee;
    const fd = new FormData();
    // Donner une extension cohérente avec le mime type
    const ext = audioBlob.type.includes('webm') ? 'webm'
              : audioBlob.type.includes('ogg') ? 'ogg'
              : audioBlob.type.includes('mp4') ? 'm4a'
              : 'webm';
    fd.append('fichier', audioBlob, `vocal_${Date.now()}.${ext}`);
    fd.append('duree_audio_sec', String(Math.max(1, Math.round(dureeSec))));
    const res = await fetch(`${API_BASE}/portail/me/chat/conversations/${convId}/messages/`, {
      method: 'POST', body: fd, headers,
    });
    let data: any = null;
    try { data = await res.json(); } catch { data = null; }
    if (!res.ok) {
      const err: any = new Error(data?.detail || `Erreur ${res.status}`);
      err.status = res.status; err.data = data;
      throw err;
    }
    return data;
  },
  // ─── Réglages / Préférences ───────────────────────────────
  async getPreferences() {
    return this._fetch('/portail/me/preferences/') as Promise<{
      pref_masquer_matricule: boolean;
      pref_invisible: boolean;
      pref_qui_peut_ecrire: 'tous' | 'ma_classe' | 'personne';
      pref_notif_silencieuses: boolean;
      pref_anniv_public: boolean;
      pref_age_public: boolean;
    }>;
  },
  async setPreferences(prefs: Partial<{
    pref_masquer_matricule: boolean;
    pref_invisible: boolean;
    pref_qui_peut_ecrire: 'tous' | 'ma_classe' | 'personne';
    pref_notif_silencieuses: boolean;
    pref_anniv_public: boolean;
    pref_age_public: boolean;
  }>) {
    return this._fetch('/portail/me/preferences/', {
      method: 'PATCH',
      body: JSON.stringify(prefs),
    }) as Promise<{ success: boolean }>;
  },
  async changerMotDePasse(ancien: string, nouveau: string) {
    return this._fetch('/portail/me/changer-mot-de-passe/', {
      method: 'POST',
      body: JSON.stringify({ ancien_password: ancien, nouveau_password: nouveau }),
    }) as Promise<{ success: boolean }>;
  },
  async deconnecterAppareils() {
    return this._fetch('/portail/me/deconnecter-appareils/', {
      method: 'POST',
    }) as Promise<{ success: boolean; nb_desactives: number }>;
  },
  async etudiantsEnLigne(scope: 'ma_classe' | 'tous' = 'ma_classe', q = '') {
    const qs = new URLSearchParams();
    qs.set('scope', scope);
    if (q) qs.set('q', q);
    return this._fetch(`/portail/me/en-ligne/?${qs.toString()}`) as Promise<{
      results: ChatEtudiantSummary[];
      nb_total: number;
      ma_classe: string;
      scope: 'ma_classe' | 'tous';
    }>;
  },
  // ─── Chat instantané avec le personnel (agents/formateurs/direction) ─────
  //     Réutilise l'API backoffice /api/messagerie-chat/ qui accepte déjà
  //     l'authentification portail (PortailEtudiantAuthentication).
  //     Ajouté 2026-08-31 pour permettre à un étudiant depuis le FAB portail
  //     d'initier une conversation avec un agent/formateur en ligne.
  async personnelListe(role: 'tous' | 'agent' | 'formateur' | 'super_admin' | 'admin' | 'directeur' | 'agent_caisse' = 'tous', q = '') {
    const qs = new URLSearchParams();
    if (role && role !== 'tous') qs.set('role', role);
    if (q) qs.set('q', q);
    const query = qs.toString();
    return this._fetch(`/messagerie-chat/contacts/${query ? '?' + query : ''}`) as Promise<{
      results: Array<{
        user_id: number; username: string; nom: string; role: string; role_label: string;
        email: string; telephone: string; photo_url: string | null;
        statut: string; en_ligne: boolean; activite: string; dernier_ping: string | null;
      }>;
      total: number;
      stats_par_role: Record<string, number>;
    }>;
  },
  async personnelOuvrirDirect(userId: number) {
    return this._fetch('/messagerie-chat/contacts/ouvrir-direct/', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    }) as Promise<{ id: number; titre_affichage: string; type_conv: string }>;
  },
  async personnelConversationsList() {
    return this._fetch('/messagerie-chat/conversations/') as Promise<{
      results: Array<{
        id: number; type_conv: string; titre_affichage: string;
        avatar_url: string | null; date_derniere_activite: string;
        nb_non_lus: number; nb_participants: number;
        dernier_message: { contenu: string; date_envoi: string; expediteur_nom: string } | null;
        participants_apercu: Array<{ user_id: number; user_nom: string; role_conv: string }>;
      }>;
      total: number;
    }>;
  },
  async personnelMessagesList(convId: number, avantId?: number) {
    const qs = new URLSearchParams();
    if (avantId) qs.set('avant_id', String(avantId));
    const q = qs.toString();
    return this._fetch(`/messagerie-chat/conversations/${convId}/messages/${q ? '?' + q : ''}`) as Promise<{
      results: Array<{
        id: number; expediteur_nom: string; expediteur_role: string;
        contenu: string; date_envoi: string; a_moi: boolean;
        type_msg: string; fichier_url?: string; fichier_nom?: string; fichier_mime?: string;
      }>;
      total: number; a_plus: boolean;
    }>;
  },
  async personnelEnvoyerMessage(convId: number, contenu: string) {
    return this._fetch(`/messagerie-chat/conversations/${convId}/messages/`, {
      method: 'POST',
      body: JSON.stringify({ contenu }),
    }) as Promise<{ id: number; contenu: string; date_envoi: string }>;
  },
  /** Image, fichier ou message vocal dans le chat avec le personnel (2026-10-03).
   *  Même point d'accès que le texte, en multipart : le serveur en déduit le type. */
  async personnelEnvoyerFichier(convId: number, fichier: Blob, nomFichier: string, contenu = '') {
    const token = this.getToken();
    const annee = localStorage.getItem('annee_academique');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (annee) headers['X-Annee-Academique'] = annee;
    const fd = new FormData();
    fd.append('fichier', fichier, nomFichier);
    if (contenu) fd.append('contenu', contenu);
    const res = await fetch(`${API_BASE}/messagerie-chat/conversations/${convId}/messages/`, {
      method: 'POST', body: fd, headers,
    });
    let data: any = null;
    try { data = await res.json(); } catch { data = null; }
    if (!res.ok) {
      const err: any = new Error(data?.detail || `Erreur ${res.status}`);
      err.status = res.status; err.data = data;
      throw err;
    }
    return data as { id: number; contenu: string; date_envoi: string };
  },
  async personnelMarquerLus(convId: number) {
    return this._fetch(`/messagerie-chat/conversations/${convId}/marquer-lus/`, {
      method: 'POST',
    }) as Promise<{ success: boolean }>;
  },

  async chatCreerGroupe(nom: string, matricules: string[]) {
    return this._fetch('/portail/me/chat/groupes/', {
      method: 'POST',
      body: JSON.stringify({ nom, matricules }),
    }) as Promise<{ conversation_id: number; nom: string; nb_participants: number }>;
  },
  async chatAjouterParticipants(convId: number, matricules: string[]) {
    return this._fetch(`/portail/me/chat/conversations/${convId}/participants/`, {
      method: 'POST',
      body: JSON.stringify({ matricules }),
    }) as Promise<{ success: boolean; ajoutes: string[]; reactives: string[] }>;
  },
  async chatRetirerParticipant(convId: number, matricule: string) {
    return this._fetch(`/portail/me/chat/conversations/${convId}/participants/`, {
      method: 'DELETE',
      body: JSON.stringify({ matricule }),
    }) as Promise<{ success: boolean; matricule_retire: string }>;
  },
  async chatQuitterGroupe(convId: number) {
    return this._fetch(`/portail/me/chat/conversations/${convId}/quitter/`, {
      method: 'POST',
    }) as Promise<{ success: boolean }>;
  },
  async chatSupprimerConversation(convId: number) {
    return this._fetch(`/portail/me/chat/conversations/${convId}/`, {
      method: 'DELETE',
    }) as Promise<{ success: boolean }>;
  },
  async chatListBlocages() {
    return this._fetch('/portail/me/chat/blocages/') as Promise<{
      results: { matricule_bloque: string; date_blocage: string; motif: string; etudiant: ChatEtudiantSummary }[];
      total: number;
    }>;
  },
  async chatBloquer(matricule: string, motif?: string) {
    return this._fetch('/portail/me/chat/blocages/', {
      method: 'POST',
      body: JSON.stringify({ matricule, motif }),
    }) as Promise<{ success: boolean }>;
  },
  async chatDebloquer(matricule: string) {
    return this._fetch('/portail/me/chat/blocages/', {
      method: 'DELETE',
      body: JSON.stringify({ matricule }),
    }) as Promise<{ success: boolean }>;
  },
  async chatSignalerMessage(msgId: number, motif: string) {
    return this._fetch(`/portail/me/chat/messages/${msgId}/signaler/`, {
      method: 'POST',
      body: JSON.stringify({ motif }),
    }) as Promise<{ success: boolean; signalement_id?: number }>;
  },
  async chatSupprimerMessage(msgId: number) {
    return this._fetch(`/portail/me/chat/messages/${msgId}/`, {
      method: 'DELETE',
    }) as Promise<{ success: boolean }>;
  },
  async chatEpinglerMessage(msgId: number) {
    return this._fetch(`/portail/me/chat/messages/${msgId}/epingler/`, {
      method: 'POST',
    }) as Promise<{ success: boolean; epingle: boolean }>;
  },
  async chatTyping(convId: number, action: 'typing' | 'recording' | 'stop') {
    return this._fetch(`/portail/me/chat/conversations/${convId}/typing/`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }) as Promise<{ success: boolean }>;
  },
  async chatTransfererMessage(msgId: number, payload: { conversation_ids?: number[]; matricules?: string[] }) {
    return this._fetch(`/portail/me/chat/messages/${msgId}/transferer/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }) as Promise<{ success: boolean; nb_envoyes: number }>;
  },

  // ─── Actualités (fil portail) ──────────────────────────────
  async actualitesList() {
    return this._fetch('/portail/me/actualites/') as Promise<{
      results: ActualiteItem[];
      nb_non_lus: number;
    }>;
  },
  async actualiteDetail(id: number) {
    return this._fetch(`/portail/me/actualites/${id}/`) as Promise<ActualiteItem>;
  },
  async actualiteReaction(id: number, type_reaction: string | null) {
    return this._fetch(`/portail/me/actualites/${id}/reaction/`, {
      method: 'POST',
      body: JSON.stringify(type_reaction ? { type_reaction } : {}),
    }) as Promise<{ success: boolean; ma_reaction: string | null }>;
  },

  async actualiteCommentairesList(id: number) {
    return this._fetch(`/portail/me/actualites/${id}/commentaires/`) as Promise<{
      results: Array<{
        id: number; auteur_type: string; auteur_nom: string;
        contenu: string; date_creation: string; a_moi: boolean;
        parent_id_commentaire: number | null;
      }>;
      total: number;
    }>;
  },
  async actualiteCommentaireCreer(id: number, contenu: string, parentId?: number) {
    return this._fetch(`/portail/me/actualites/${id}/commentaires/`, {
      method: 'POST',
      body: JSON.stringify({ contenu, parent_id_commentaire: parentId ?? null }),
    }) as Promise<{ success: true; id: number }>;
  },
  async actualiteCommentaireSupprimer(cId: number) {
    return this._fetch(`/portail/me/actualites/commentaires/${cId}/`, {
      method: 'DELETE',
    }) as Promise<{ success: true }>;
  },
  async actualitePublier(fd: FormData) {
    const token = this.getToken();
    const annee = localStorage.getItem('annee_academique');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (annee) headers['X-Annee-Academique'] = annee;
    const res = await fetch(`${API_BASE}/portail/me/actualites/publier/`, {
      method: 'POST',
      body: fd,
      headers,
    });
    let data: any = null;
    try { data = await res.json(); } catch { /* noop */ }
    if (!res.ok) {
      const err: any = new Error(data?.detail || `Erreur ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return data as ActualiteItem;
  },
  async actualiteMienneSupprimer(id: number) {
    return this._fetch(`/portail/me/actualites/${id}/supprimer/`, {
      method: 'DELETE',
    }) as Promise<{ success: true }>;
  },

  async chatAnnuaire(params: { q?: string; classe?: string; limit?: number } = {}) {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.classe) qs.set('classe', params.classe);
    if (params.limit) qs.set('limit', String(params.limit));
    return this._fetch(`/portail/me/chat/annuaire/?${qs.toString()}`) as Promise<{
      results: (ChatEtudiantSummary & { en_ligne: boolean })[];
      total: number;
      ma_classe: string;
    }>;
  },

  async repondreComplementDemande(demandeId: number, photo: File, message?: string) {
    const token = this.getToken();
    const annee = localStorage.getItem('annee_academique');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (annee) headers['X-Annee-Academique'] = annee;
    const fd = new FormData();
    fd.append('photo', photo);
    if (message) fd.append('message_etudiant', message);
    const res = await fetch(
      `${API_BASE}/portail/me/demandes-activation/${demandeId}/complement/`,
      { method: 'POST', body: fd, headers }
    );
    let data: any = null;
    try { data = await res.json(); } catch {}
    if (!res.ok) {
      const err: any = new Error(data?.detail || `Erreur ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return data;
  },

  async genererCodeVerification(semestre: number) {
    return this._fetch('/portail/me/releve/code-verification/', {
      method: 'POST',
      body: JSON.stringify({ semestre }),
    }) as Promise<{ code: string; date_generation: string }>;
  },

  async getCopiesScan(matiereId: number) {
    return this._fetch(`/portail/me/copies-scan/?matiere_id=${matiereId}`) as Promise<{
      matiere: { id: number; code: string; nom: string } | null;
      scans: Array<{
        type: 'devoir' | 'examen' | 'rattrapage';
        libelle: string;
        image_base64: string | null;
        numero_ligne: number | null;
        numero_page: number | null;
        code_releve: string;
        date_scan: string | null;
      }>;
      scans_desactives?: boolean;
      message?: string;
    }>;
  },

  async historique() {
    return this._fetch('/portail/me/historique/') as Promise<{
      results: HistoriqueAnnee[];
      blocage: HistoriqueBlocageInfo | null;
      evolution?: {
        annee: string; niveau: string; semestre: number; label: string;
        moyenne: number; valide: boolean;
      }[];
      stats?: {
        moyenne_cumulee: number; nb_semestres: number; nb_valides: number;
        meilleur_semestre: { label: string; annee: string; moyenne: number };
        semestre_faible: { label: string; annee: string; moyenne: number };
        tendance: 'hausse' | 'baisse' | 'stable';
        derniere_moyenne: number;
      } | null;
      points_forts?: {
        matieres_fortes: { nom: string; ue: string; moyenne: number; annee: string; label: string }[];
        matieres_faibles: { nom: string; ue: string; moyenne: number; annee: string; label: string }[];
        meilleure_ue: { nom: string; moyenne: number; annee: string; label: string } | null;
      } | null;
    }>;
  },

  async contactInfo() {
    const res = await fetch(`${API_BASE}/portail/contact/`);
    if (!res.ok) return {};
    return res.json() as Promise<ContactInfo>;
  },

  async getEmploisTemps() {
    return this._fetch('/portail/me/emplois-temps/') as Promise<{
      etudiant: { matricule: string; nom: string; prenoms: string; classe: string; niveau: string; option: string };
      sessions: EmploiTempsSession[];
      nb_sessions: number;
    }>;
  },

  async creerSuggestion(payload: {
    categorie: string;
    titre?: string;
    contenu: string;
    anonyme: boolean;
    cible_formateur_nom?: string;
    cible_matiere?: string;
  }) {
    return this._fetch('/portail/me/suggestion/', {
      method: 'POST',
      body: JSON.stringify(payload),
    }) as Promise<{ success: boolean; id: number; anonyme: boolean; message: string }>;
  },

  async getSujetsExamens(filters?: {
    matiere?: string; niveau?: string; annee_academique?: string; option?: string;
  }) {
    const params = new URLSearchParams();
    if (filters?.matiere) params.set('matiere', filters.matiere);
    if (filters?.niveau) params.set('niveau', filters.niveau);
    if (filters?.annee_academique) params.set('annee_academique', filters.annee_academique);
    if (filters?.option) params.set('option', filters.option);
    const qs = params.toString();
    return this._fetch(`/portail/me/sujets-examens/${qs ? '?' + qs : ''}`) as Promise<{
      results: Array<{
        id: number;
        titre: string;
        description: string;
        matiere_libelle: string;
        matiere_code: string;
        niveau: string;
        option: string;
        annee_academique: string;
        semestre: string;
        session: 'normale' | 'rattrapage';
        fichier_url: string | null;
        taille_octets: number;
        date_creation: string;
        nb_telechargements: number;
      }>;
      total: number;
      filtres_dispo: { annees: string[]; options: string[] };
      etudiant: { matricule: string; niveau: string; option: string };
    }>;
  },

  async telechargerSujetExamen(sujetId: number) {
    return this._fetch(`/portail/me/sujets-examens/${sujetId}/telecharger/`, {
      method: 'POST',
    }) as Promise<{ success: boolean; fichier_url: string | null; nom_original: string }>;
  },

  async getSupportsCours(semestre?: number) {
    const qs = semestre ? `?semestre=${semestre}` : '';
    return this._fetch(`/portail/me/supports-cours/${qs}`) as Promise<{
      etudiant: { matricule: string; nom: string; prenoms: string; classe: string; niveau: string; option: string };
      groupes: Array<{
        matiere_id: number | null;
        matiere_libelle: string;
        semestre: number | null;
        supports: Array<{
          id: number;
          titre: string;
          description: string;
          nom_original: string;
          type_fichier: 'pdf' | 'word' | 'autre';
          taille_octets: number;
          taille_humaine: string;
          ordre: number;
          formateur_nom: string;
          nb_telechargements: number;
          date_creation: string;
        }>;
      }>;
      total: number;
    }>;
  },

  /** URL absolue pour télécharger un support (avec auth en query string). */
  urlTelechargerSupport(supportId: number): string {
    const token = this.getToken();
    const base = (import.meta as any).env?.VITE_API_BASE || API_BASE;
    return `${base}/portail/me/supports-cours/${supportId}/telecharger/?token=${encodeURIComponent(token || '')}`;
  },

  async getMessagesFormateur() {
    return this._fetch('/portail/me/messages-formateur/') as Promise<{
      results: Array<{
        id: number;
        formateur_nom: string;
        portee: 'individuel' | 'classe';
        classe: string;
        sujet: string;
        contenu: string;
        matiere_libelle: string;
        date_envoi: string;
        lu: boolean;
      }>;
      total: number;
    }>;
  },

  async getEdtHebdo(periode: 'pair' | 'impair' = 'pair') {
    return this._fetch(`/portail/me/emploi-temps-hebdo/?periode=${periode}`) as Promise<{
      etudiant: { matricule: string; nom: string; prenoms: string; classe: string; niveau: string; option: string };
      periode: 'pair' | 'impair';
      semestre_local: number;
      creneaux: Array<{
        id: number;
        jour: string;
        jour_label: string;
        heure_debut: string;
        heure_fin: string;
        matiere: string;
        code_matiere: string;
        formateur: string;
        salle: string;
        semestre: number | null;
        tronc_commun_groupe: string;
      }>;
      total: number;
      disponible: boolean;
      message: string;
    }>;
  },

  async getBrassages() {
    return this._fetch('/portail/me/brassages/') as Promise<{
      etudiant: { matricule: string; nom: string; prenoms: string; classe: string };
      sessions: BrassageSession[];
      nb_sessions: number;
    }>;
  },

  async searchBrassage(brassageId: number, q: string) {
    return this._fetch(`/portail/me/brassages/${brassageId}/search/?q=${encodeURIComponent(q)}`) as Promise<{
      results: { matricule: string; nom_complet: string; classe: string; site_nom: string; salle_nom: string }[];
      nb: number;
      query: string;
    }>;
  },

  async envoyerMessageScolarite(payload: {
    sujet?: string;
    message: string;
    thread_id?: number;
    fichier?: File;
    duree_audio_sec?: number;
  }) {
    if (payload.fichier) {
      // FormData : pas de Content-Type imposé, le navigateur ajoute le boundary
      const token = this.getToken();
      const annee = localStorage.getItem('annee_academique');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (annee) headers['X-Annee-Academique'] = annee;
      const fd = new FormData();
      if (payload.sujet) fd.append('sujet', payload.sujet);
      fd.append('message', payload.message);
      if (payload.thread_id) fd.append('thread_id', String(payload.thread_id));
      fd.append('fichier', payload.fichier);
      if (payload.duree_audio_sec) fd.append('duree_audio_sec', String(payload.duree_audio_sec));
      const res = await fetch(`${API_BASE}/portail/me/contact-scolarite/`, {
        method: 'POST', body: fd, headers,
      });
      let data: any = null;
      try { data = await res.json(); } catch { data = null; }
      if (!res.ok) {
        const err: any = new Error(data?.detail || `Erreur ${res.status}`);
        err.status = res.status; err.data = data;
        throw err;
      }
      return data as { success: true; thread_id: number; type: string };
    }
    return this._fetch('/portail/me/contact-scolarite/', {
      method: 'POST',
      body: JSON.stringify(payload),
    }) as Promise<{ success: true; thread_id: number }>;
  },

  async listMessagesScolarite() {
    return this._fetch('/portail/me/messages-scolarite/') as Promise<{
      results: ThreadMessageScolarite[];
      nb_total_non_lus: number;
    }>;
  },

  async getMessageScolariteThread(threadId: number) {
    return this._fetch(`/portail/me/messages-scolarite/${threadId}/`) as Promise<{
      thread: { id: number; sujet: string; date_creation: string; derniere_activite: string; traite: boolean };
      messages: PostMessageItem[];
    }>;
  },

  async supprimerPostMessageScolarite(postId: number) {
    return this._fetch(`/portail/me/messages-scolarite/posts/${postId}/`, {
      method: 'DELETE',
    }) as Promise<{ success: boolean }>;
  },

  async verifierCodePublic(code: string) {
    // public - pas de token
    const res = await fetch(`${API_BASE}/portail/verifier/${code.toUpperCase()}/`);
    const data = await res.json();
    if (!res.ok) {
      const err: any = new Error(data?.detail || 'Code invalide');
      err.status = res.status;
      throw err;
    }
    return data as {
      valide: boolean;
      code: string;
      matricule: string;
      nom_complet: string;
      classe: string;
      semestre: number;
      annee_academique: string;
      moyenne_semestre: string | null;
      semestre_valide: boolean;
      date_generation: string;
      nb_consultations: number;
    };
  },

  // ─── Fil d'entraide ─────────────────────────────────────────────
  async entraideListe(params: {
    q?: string; categorie?: string; niveau?: string; matiere?: string;
    resolu?: '1' | '0'; ordre?: 'recent' | 'populaire'; limit?: number;
  } = {}): Promise<{ results: EntraidePostItem[]; total: number }> {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) usp.set(k, String(v)); });
    const qs = usp.toString();
    return this._fetch(`/portail/entraide/${qs ? '?' + qs : ''}`);
  },
  async entraideDetail(postId: number): Promise<EntraidePostItem> {
    return this._fetch(`/portail/entraide/${postId}/`);
  },
  async entraideCreer(data: {
    titre: string; contenu: string; categorie?: string;
    matiere_liee?: string; niveau?: string;
  }): Promise<EntraidePostItem> {
    return this._fetch(`/portail/entraide/`, {
      method: 'POST', body: JSON.stringify(data),
    });
  },
  async entraideModifier(postId: number, data: Partial<{
    titre: string; contenu: string; categorie: string;
    matiere_liee: string; niveau: string;
  }>): Promise<EntraidePostItem> {
    return this._fetch(`/portail/entraide/${postId}/`, {
      method: 'PATCH', body: JSON.stringify(data),
    });
  },
  async entraideSupprimer(postId: number): Promise<{ ok: boolean }> {
    return this._fetch(`/portail/entraide/${postId}/`, { method: 'DELETE' });
  },
  async entraideToggleResolu(postId: number): Promise<{ resolu: boolean }> {
    return this._fetch(`/portail/entraide/${postId}/resoudre/`, { method: 'POST' });
  },
  async entraideVote(postId: number): Promise<{ a_vote: boolean; nb_votes: number }> {
    return this._fetch(`/portail/entraide/${postId}/vote/`, { method: 'POST' });
  },
  async entraideRepondre(postId: number, contenu: string): Promise<EntraideReponseItem> {
    return this._fetch(`/portail/entraide/${postId}/repondre/`, {
      method: 'POST', body: JSON.stringify({ contenu }),
    });
  },
  async entraideSignaler(postId: number, raison: string, commentaire?: string): Promise<{ ok: boolean }> {
    return this._fetch(`/portail/entraide/${postId}/signaler/`, {
      method: 'POST', body: JSON.stringify({ raison, commentaire: commentaire || '' }),
    });
  },
  async entraideReponseVote(reponseId: number): Promise<{ a_vote: boolean; nb_votes: number }> {
    return this._fetch(`/portail/entraide/reponse/${reponseId}/vote/`, { method: 'POST' });
  },
  async entraideReponseSupprimer(reponseId: number): Promise<{ ok: boolean }> {
    return this._fetch(`/portail/entraide/reponse/${reponseId}/`, { method: 'DELETE' });
  },
  async entraideReponseAccepter(reponseId: number): Promise<{ acceptee: boolean }> {
    return this._fetch(`/portail/entraide/reponse/${reponseId}/accepter/`, { method: 'POST' });
  },
};

export interface EntraidePostItem {
  id: number;
  auteur_matricule: string;
  auteur_nom: string;
  auteur_classe: string;
  categorie: 'question' | 'ressource' | 'annonce';
  titre: string;
  contenu: string;
  matiere_liee: string;
  niveau: string;
  resolu: boolean;
  nb_reponses: number;
  nb_votes: number;
  a_vote: boolean;
  suis_auteur: boolean;
  date_creation: string;
  date_modification: string;
  reponses?: EntraideReponseItem[];
}

export interface EntraideReponseItem {
  id: number;
  post_id: number;
  auteur_matricule: string;
  auteur_nom: string;
  auteur_classe: string;
  contenu: string;
  acceptee: boolean;
  nb_votes: number;
  a_vote: boolean;
  suis_auteur: boolean;
  date_creation: string;
}

export interface FenetreReclamation {
  ouvertes: boolean;
  raison?: 'non_publie' | 'expire';
  date_publication: string | null;
  date_limite: string | null;
  jours_restants: number | null;
  jours_total: number;
}

export interface ReclamationItem {
  id: number;
  type_reclamation: 'devoir' | 'examen' | 'autre';
  matiere_nom: string;
  semestre: string;
  note_actuelle: string | null;
  note_attendue: string | null;
  motif: string;
  statut: 'deposee' | 'en_examen' | 'acceptee' | 'rejetee' | 'cloturee';
  cloturee?: boolean;
  reponse_scolarite: string;
  date_creation: string;
  date_traitement: string | null;
  derniere_activite?: string;
  nb_messages?: number;
  nb_non_lus?: number;
}

export interface MessageReclamationItem {
  id: number;
  expediteur: 'etudiant' | 'scolarite' | 'systeme';
  expediteur_nom: string;
  contenu: string;
  date_creation: string;
}

export interface ThreadMessageScolarite {
  id: number;
  sujet: string;
  dernier_apercu: string;
  dernier_expediteur: 'etudiant' | 'scolarite';
  derniere_activite: string;
  date_creation: string;
  date_dernier_msg: string;
  nb_non_lus: number;
  traite: boolean;
  a_reponse_scolarite: boolean;
}

export interface PostMessageItem {
  id: string | number;
  expediteur: 'etudiant' | 'scolarite';
  expediteur_nom: string;
  contenu: string;
  type?: 'texte' | 'image' | 'audio' | 'fichier';
  fichier_url?: string | null;
  fichier_nom_original?: string;
  duree_audio_sec?: number;
  date_creation: string;
  lu_par_destinataire?: boolean;
  date_lecture?: string | null;
  supprime?: boolean;
  supprime_par?: string;
  peut_supprimer?: boolean;
}

export interface DemandeActivationItem {
  id: number;
  type_demande: 'paiement' | 'gratuite' | 'bourse' | 'autre';
  statut: 'en_attente' | 'complement_demande' | 'acceptee' | 'rejetee' | 'cloturee';
  periodes_concernees: string[];
  periodes_validees: string[];
  montant_declare: string | null;
  montant_valide: string | null;
  photo_url: string | null;
  photo_complement_url: string | null;
  reference_recu: string;
  mode_paiement: string;
  date_paiement_declaree: string | null;
  message_etudiant: string;
  message_caisse: string;
  motif_rejet: string;
  date_creation: string;
  date_traitement: string | null;
  traite_par_nom: string;
}

export interface NotificationItem {
  id: number;
  titre: string;
  message: string;
  type_notif: string;
  lien: string;
  lu: boolean;
  date_creation: string;
}

export interface CreneauExamen {
  id: number;
  date: string | null;
  jour_label: string;
  heure_debut: string;
  heure_fin: string;
  matiere_nom: string;
  enseignant_noms: string[];
  groupe_label: string;
  commentaire: string;
}

export interface EmploiTempsSession {
  id: number;
  nom: string;
  annee_academique: string;
  semestre: number;
  type_session: string;
  type_session_label: string;
  date_debut: string | null;
  date_fin: string | null;
  date_visibilite: string | null;
  nombre_creneaux_etudiant: number;
  creneaux: CreneauExamen[];
  concerne_etudiant: boolean;
}

export interface BrassageSession {
  id: number;
  description: string;
  mode: string;
  mode_display: string;
  date_generation: string;
  date_visibilite: string | null;
  nombre_etudiants: number;
  nombre_salles: number;
  mon_placement: { site_nom: string; salle_nom: string; classe: string } | null;
  nb_camarades_meme_salle: number;
  camarades_meme_salle: { matricule: string; nom_complet: string; classe: string }[];
}

export interface ReleveMatiere {
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  credits: number;
  note_d1: number | null;
  note_d2: number | null;
  absent_d1: boolean;
  absent_d2: boolean;
  moy_devoirs: number | null;
  /** Note d'examen retenue pour le calcul (= max entre session 1 et rattrapage) */
  note_examen: number | null;
  absent_examen: boolean;
  /** True si la note retenue vient de la session de rattrapage */
  est_rattrapage: boolean;
  /** Note brute de la session ordinaire (peut être null si pas composée) */
  note_examen_s1?: number | null;
  absent_examen_s1?: boolean;
  /** Note brute de la session de rattrapage (peut être null si pas repassée) */
  note_examen_rattrapage?: number | null;
  absent_examen_rattrapage?: boolean;
  moyenne_ecue: number | null;
  validee: boolean;
}

export interface ReleveUE {
  ue_id: number;
  code_ue: string;
  nom_ue: string;
  validee: boolean;
  validee_par_compensation?: boolean;
  validee_en_rattrapage?: boolean;
  /** Étiquette session : 'ordinaire' | 'rattrapage' | 'compensation' | '' (non validée) */
  session_label?: 'ordinaire' | 'rattrapage' | 'compensation' | '';
  compensable?: boolean;
  eliminatoire?: boolean;
  moyenne_ue: number | null;
  credits_obtenus: number;
  credits_total: number;
  session_validation: string;
  matieres: ReleveMatiere[];
}

export interface ReleveResultatSemestre {
  moyenne_semestre: number | null;
  credits_obtenus: number;
  credits_requis: number;
  valide: boolean;
  rang: number | null;
  effectif_classe: number | null;
  session_validation: string;
}

export interface ReleveData {
  // Notes d'examens masquées par l'admin : décision, moyennes et rang absents
  examens_masques?: boolean;
  examens_masques_message?: string;
  etudiant: {
    id: number;
    matricule: string;
    nom: string;
    prenoms: string;
    date_naissance: string | null;
    lieu_naissance: string;
    niveau: string;
    option: string;
    classe: string;
    cycle: string;
    photo_url: string | null;
  };
  annee_academique: string;
  semestre: number;
  ues: ReleveUE[];
  resultat_semestre: ReleveResultatSemestre | null;
  provisoire: boolean;
  // Infos institut selon le campus (2026-10-10) : tél, e-mail, adresse, ville, chef.
  institut?: {
    deployment?: string; ville?: string; telephone?: string; email?: string;
    adresse_rue?: string; chef_scolarite?: string; chef_scolarite_titre?: string;
  };
  stats_classe?: {
    effectif: number;
    moyenne_classe: number;
    mediane: number;
    min: number;
    max: number;
    percentile: number | null;
    au_dessus_mediane: boolean | null;
    position_estimee: number | null;
  } | null;
  astuces?: { type: 'success' | 'warning' | 'danger' | 'info'; icon: string; texte: string }[];
}

export interface HistoriqueSemestre {
  semestre: number;
  moyenne_semestre: number | null;
  credits_obtenus: number | null;
  credits_requis: number | null;
  valide: boolean | null;
  mention: string | null;
  rang: number | null;
  bloque?: boolean;
  bloque_parite?: boolean;
  message?: string;
  session_annulee?: boolean; // session ordinaire annulée par sanction disciplinaire
  en_attente?: boolean; // Semestre annoncé mais pas encore calculé (S2 en cours)
}

export interface HistoriqueAnnee {
  annee_academique: string;
  niveau: string;
  classe: string;
  option: string;
  actif: boolean;
  semestres: HistoriqueSemestre[];
}

export interface HistoriqueBlocageInfo {
  actif: boolean;
  message: string;
  depuis: string | null;
  raison: string;
}

export interface ContactInfo {
  whatsapp?: string;
  email?: string;
  tel?: string;
  adresse?: string;
}

// ─── Chat étudiants ─────────────────────────────────────────────
export interface ChatEtudiantSummary {
  matricule: string;
  /** Matricule réel (non masqué) — à utiliser pour les actions (ouvrir conv, bloquer).
   *  Le champ `matricule` peut contenir '••••••••' selon la politique de confidentialité. */
  matricule_reel?: string;
  matricule_masque?: boolean;
  nom: string;
  prenoms: string;
  photo_url: string | null;
  classe: string;
  option?: string;
  niveau?: string;
}

export interface ChatConversationItem {
  id: number;
  type: 'direct' | 'groupe';
  titre: string;
  sous_titre: string;
  avatar_url: string | null;
  autres_participants: ChatEtudiantSummary[];
  dernier_message: {
    contenu: string;
    date_envoi: string | null;
    est_de_moi: boolean;
    supprime: boolean;
  } | null;
  nb_non_lus: number;
  en_ligne: boolean;
  date_creation: string;
}

export interface ChatReplyPreview {
  id: number;
  expediteur_nom: string;
  est_de_moi: boolean;
  type: string;
  apercu: string;
  supprime: boolean;
}

export interface ChatMessageItem {
  id: number;
  expediteur: ChatEtudiantSummary;
  est_de_moi: boolean;
  type: 'texte' | 'image' | 'fichier' | 'audio';
  contenu: string;
  fichier_url: string | null;
  fichier_nom: string;
  duree_audio_sec?: number;
  date_envoi: string;
  supprime: boolean;
  /** Statut de lecture pour mes propres messages :
   *  'envoye'      → un seul check (juste envoyé, personne n'a encore lu)
   *  'lu_partiel'  → double check (au moins une personne a lu — utile en groupe)
   *  'lu'          → double check bleu (tout le monde a lu) */
  statut?: 'envoye' | 'lu_partiel' | 'lu' | null;
  reply?: ChatReplyPreview | null;
  transfere_de?: string | null;
  epingle?: boolean;
}

export interface ChatEpingleItem {
  id: number;
  expediteur_nom: string;
  apercu: string;
  date_envoi: string;
}

export interface ActualiteItem {
  id: number;
  type: string;
  type_label: string;
  titre: string;
  contenu: string;
  image_url: string | null;
  video_url?: string | null;
  auteur_role?: string;
  auteur_classe?: string;      // Fix 2026-09-07 : classe si l'auteur est étudiant
  auteur_matricule?: string;   // Fix 2026-09-07 : matricule si l'auteur est étudiant
  lien_externe: string;
  date_evenement: string | null;
  lieu: string;
  date_creation: string;
  date_modification?: string;
  auteur_nom: string;
  epinglee: boolean;
  publiee: boolean;
  reactions: Record<string, number>;
  nb_reactions_total: number;
  ma_reaction: string | null;
  a_lu: boolean;
  tous_etudiants: boolean;
  niveaux_cibles: string[];
  classes_cibles: string[];
  options_cibles: string[];
  nb_commentaires?: number;    // Fix 2026-09-07 : pour l'affichage sticky footer
}
